import { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Container, Alert, CircularProgress, Stack, Button, Typography, Paper } from '@mui/material'
import { Refresh as RefreshIcon, Receipt as AnalyserIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useDatabase } from '@/hooks/useDatabase'
import KPICards from '@/components/KPICards'
import FilterPanel from '@/components/FilterPanel'
import { ExcelImport } from '@/components/ExcelImport'
import { MonthlyNettoChart, TopSuppliersChart } from '@/components/Charts'
import InvoiceTable from '@/components/InvoiceTable'
import { InvoiceFilter, InvoiceRow } from '@/types'
import { filterInvoices, calculateKPIs, groupByMonth, getTopSuppliers } from '@/utils/filterUtils'

export default function DashboardPage() {
  const navigate = useNavigate()
  const [uploadedInvoices, setUploadedInvoices] = useState<InvoiceRow[] | null>(null)
  const [filter, setFilter] = useState<InvoiceFilter>({
    supplierNames: [],
    dateRange: { start: null, end: null },
    bruttoRange: { min: null, max: null },
    nettoRange: { min: null, max: null },
    rechnungsnummerSearch: '',
    commentSearch: '',
  })

  const { getInvoices } = useDatabase()

  // Database integration state
  const [dbInvoices, setDbInvoices] = useState<InvoiceRow[]>([])
  const [isUsingDb, setIsUsingDb] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch from database on mount
  const loadFromDatabase = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await getInvoices({ limit: 10000 })
      // Map DatabaseInvoice to InvoiceRow
      const mapped = result.data.map(db => {
        // Parse DD.MM.YYYY or ISO format to Date object
        let dateInvoice: Date | null = null
        if (db.dateInvoice) {
          // Try DD.MM.YYYY format
          const dmyMatch = db.dateInvoice.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
          if (dmyMatch) {
            const [, day, month, year] = dmyMatch
            dateInvoice = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
          } else {
            // Try ISO format
            dateInvoice = new Date(db.dateInvoice)
          }
          if (Number.isNaN(dateInvoice.getTime())) dateInvoice = null
        }

        return {
          supplierName: db.supplierName,
          dateInvoice,
          brutto: db.brutto,
          netto: db.netto,
          vatAmount: db.vatAmount,
          taxFreeAmount: db.taxFreeAmount,
          taxableNetAmount: db.taxableNetAmount,
          rechnungsnummer: db.rechnungsnummer,
          comment: db.comment,
        }
      })
      setDbInvoices(mapped)
      setIsUsingDb(mapped.length > 0)
    } catch (err) {
      console.error('Failed to load from database:', err)
    } finally {
      setIsLoading(false)
    }
  }, [getInvoices])

  useEffect(() => {
    void loadFromDatabase()
  }, [loadFromDatabase])

  // Use database invoices if available, otherwise uploaded invoices
  const activeInvoices = isUsingDb ? dbInvoices : (uploadedInvoices || [])

  // Handle file upload
  const handleDataLoaded = (data: InvoiceRow[]) => {
    setUploadedInvoices(data)
    setIsUsingDb(false)
    setFilter({
      supplierNames: [],
      dateRange: { start: null, end: null },
      bruttoRange: { min: null, max: null },
      nettoRange: { min: null, max: null },
      rechnungsnummerSearch: '',
      commentSearch: '',
    })
  }

  // Clear uploaded data
  const handleClearUpload = () => {
    setUploadedInvoices(null)
  }

  // Apply filters
  const filteredInvoices = useMemo(() => {
    return filterInvoices(activeInvoices, filter)
  }, [activeInvoices, filter])

  // Calculate KPIs
  const kpi = useMemo(() => {
    return calculateKPIs(filteredInvoices)
  }, [filteredInvoices])

  // Prepare chart data
  const monthlyData = useMemo(() => {
    return groupByMonth(filteredInvoices)
  }, [filteredInvoices])

  const topSuppliers = useMemo(() => {
    return getTopSuppliers(filteredInvoices, 10)
  }, [filteredInvoices])

  // Show loading state
  if (isLoading && !activeInvoices.length) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Loading data...
          </Typography>
        </Box>
      </Container>
    )
  }

  // Show initial state with upload option
  if (!activeInvoices.length) {
    return (
      <Container maxWidth="md">
        <Stack spacing={3} sx={{ mt: 4 }}>
          <Typography variant="h4" component="h1">
            Import Data
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              Upload your Excel file with invoice data. The file is processed in your browser and not uploaded to any server.
            </Typography>
          </Alert>

          <Paper sx={{ p: 3 }}>
            <ExcelImport onDataLoaded={handleDataLoaded} fileName="Invoices" />
          </Paper>

          <Button variant="outlined" onClick={loadFromDatabase} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh from Database'}
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Dashboard
          </Typography>
          <Stack direction="row" spacing={2}>
            {uploadedInvoices && (
              <Button variant="outlined" onClick={handleClearUpload}>
                Different File
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<AnalyserIcon />}
              onClick={() => navigate('/invoice-analyser')}
            >
              Invoice Analyser
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadFromDatabase}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        {/* Data source notice */}
        {isUsingDb && (
          <Alert severity="info">
            {dbInvoices.length} invoices from database.
          </Alert>
        )}

        {uploadedInvoices && (
          <Alert severity="success">
            {uploadedInvoices.length} invoices from uploaded file.
          </Alert>
        )}

        {/* KPI Cards */}
        <KPICards kpi={kpi} isLoading={isLoading} />

        {/* Charts Row */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 0' }, minWidth: 0 }}>
            <MonthlyNettoChart data={monthlyData} isLoading={isLoading} />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 0' }, minWidth: 0 }}>
            <TopSuppliersChart data={topSuppliers} isLoading={isLoading} />
          </Box>
        </Box>

        {/* Filter and Table */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
            <FilterPanel
              invoices={activeInvoices}
              filter={filter}
              onFilterChange={setFilter}
              filteredCount={filteredInvoices.length}
              totalCount={activeInvoices.length}
            />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <InvoiceTable invoices={filteredInvoices} isLoading={isLoading} />
          </Box>
        </Box>
      </Stack>
    </Container>
  )
}
